import * as THREE from 'three'
import CompassRing from './CompassRing';
import { calculateDelta } from './Helpers';
import { FACES } from "../Global/materials";

// Controller class for managing the interactive behavior of the compass ring
export default class CompassRingController extends THREE.EventDispatcher {
    constructor(camera, domElement) {
        super();
        this._camera = camera; // Reference to the main camera
        this.domElement = domElement; // DOM element used for mouse event listening
        this._compass = new CompassRing(); // The visual compass ring object
        this._animation = null; // Used to store animation state for smooth rotation
        // Bind event handlers to maintain context
        this._handleMouseMove = this._handleMouseMove.bind(this);
        this._handleMouseClick = this._handleMouseClick.bind(this);
        this._listen(); // Set up event listeners
    }

    // Add mouse event listeners for interaction
    _listen() {
        this.domElement.addEventListener('mousemove', this._handleMouseMove);
        this.domElement.addEventListener('click', this._handleMouseClick);
    }

    // Handle mouse move event to trigger hover effects
    _handleMouseMove(event) {
        const x = (event.offsetX / event.target.clientWidth) * 2 - 1;
        const y = -(event.offsetY / event.target.clientHeight) * 2 + 1;
        this._checkSideOver(x, y);
    }

    // Handle hover logic by casting a ray from mouse position into the scene
    _checkSideOver(x, y) {
        const raycaster = new THREE.Raycaster();
        raycaster.setFromCamera({ x, y }, this._camera);
        const intersects = raycaster.intersectObjects(this._compass.children, true);

        // Reset all face colors to their original
        this._compass.traverse(function (obj) {
            if (obj.name) {
                obj.material.color.setHex(obj.userData.color);
            }
        });

        // Apply hover color to the intersected face
        if (intersects.length) {
            for (let { object } of intersects) {
                if (object.name) {
                    object.parent.children.forEach(function (child) {
                        if (child.name === object.name) {
                            child.material.color.setHex(child.userData.hover);
                        }
                    });
                    break; // Exit after applying to first match
                }
            }
        }
    }

    // Handle mouse click event for rotation
    _handleMouseClick(event) {
        const x = (event.offsetX / event.target.clientWidth) * 2 - 1;
        const y = -(event.offsetY / event.target.clientHeight) * 2 + 1;
        this._checkSideTouch(x, y);
    }

    // Check which face was clicked and trigger compass rotation
    _checkSideTouch(x, y) {
        const raycaster = new THREE.Raycaster();
        raycaster.setFromCamera({ x, y }, this._camera);
        const intersects = raycaster.intersectObjects(this._compass.children, true);
        if (intersects.length) {
            for (let { object } of intersects) {
                if (object.name) {
                    this._rotateCompass(object.name); // Rotate based on face clicked
                    break;
                }
            }
        }
    }

    // Determine the target rotation based on the face name
    _rotateCompass(side) {
        switch (side) {
            case FACES.south:
                this._setCompassAngles(0, FACES.south);
                break;
            case FACES.east:
                this._setCompassAngles(Math.PI * 1.5, FACES.east);
                break;
            case FACES.north:
                this._setCompassAngles(Math.PI, FACES.north);
                break;
            case FACES.west:
                this._setCompassAngles(Math.PI / 2, FACES.west);
                break;
            default:
                break;
        }
    }

    // Set up animation state for rotating the compass smoothly
    _setCompassAngles(angle, face) {
        const base = this._compass.rotation;
        this._animation = {
            delta: calculateDelta(base.z, angle), // Use shortest angular path
            duration: 500, // Animation duration in ms
            time: Date.now(), // Start time
            face: face,
            done: 0 // Amount of time elapsed
        };
    }

    // Public method to be called on each frame to update animation
    update() {
        this._animate();
    }

    // Animate the compass ring's rotation
    _animate() {
        if (!this._animation) return;
        const now = Date.now();
        const { time, done, duration } = this._animation;
        let chunk = now - time; // Time since last frame

        // Clamp to prevent overshooting
        if (chunk + done > duration) chunk = duration - done;

        const angle = this._animateCompassRotation(this._animation, chunk); // Rotate compass
        this._animation.done += chunk;
        this._animation.time = now;

        // Stop animation once duration is reached
        if (done === duration) this._animation = null;

        // Notify listeners of angle change
        this.dispatchEvent({
            type: 'compass-angle-change',
            face: this._animation ? this._animation.face : null,
            angle: angle
        });
    }

    // Rotate the compass ring using an easing function over time
    _animateCompassRotation({ delta, duration }, time) {
        const ease = delta / duration;
        this._compass.rotateOnAxis(new THREE.Vector3(0, 0, 1), ease * time);
        return ease * time; // Return amount of rotation applied
    }

    // Get the compass ring object for adding to the scene
    getObject() {
        return this._compass;
    }
}
